import { ConstantsOpenPose } from '@/pages/page_open_pose/constants_open_pose';
import { EOpenPoseTransformControlsMode } from '@/pages/page_open_pose/enums/e_open_pose_transform_controls_mode';
import { EOpenPoseTransformControlsSpace } from '@/pages/page_open_pose/enums/e_open_pose_transform_controls_space';
import { OpenPoseBillboardController } from '@/pages/page_open_pose/open_pose_billboard/open_pose_billboard_controller';
import { OpenPoseBodyController } from '@/pages/page_open_pose/open_pose_body/open_pose_body_controller';
import { open_pose_body_create_template } from '@/pages/page_open_pose/open_pose_body/utils/open_pose_body_create_template';
import { OpenPoseBodyProjectionController } from '@/pages/page_open_pose/open_pose_body_projection/open_pose_body_projection_controller';
import { OpenPoseCameraController } from '@/pages/page_open_pose/open_pose_camera/open_pose_camera_controller';
import { store } from '@/state/store';
import { LOCAL__ui_set_boolean } from '@/state/ui/_base/LOCAL__ui_set_boolean';
import { not_implemented } from '@shared/asserts/utils_asserts';
import * as THREE from 'three';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js';

const {
  CLEAR_COLOR,
  GRID_HELPER_SIZE,
  GRID_HELPER_DIVISIONS,
  DIRECTIONSL_LIGHT_COLOR,
  DIRECTIONSL_LIGHT_INTENSITY,
  AMBIENT_LIGHT_COLOR,
  AMBIENT_LIGHT_INTENSITY,
  DIRECTIONAL_LIGHT_INIT_POSITION_X,
  DIRECTIONAL_LIGHT_INIT_POSITION_Y,
  DIRECTIONAL_LIGHT_INIT_POSITION_Z,
  CAMERA_INIT_POSITION_X,
  CAMERA_INIT_POSITION_Y,
  CAMERA_INIT_POSITION_Z,
  CAMERA_INIT_FAR,
  CAMERA_INIT_NEAR,
  CAMERA_INIT_FOV,
} = ConstantsOpenPose;

class ServiceOpenPose {
  /*
   * FIELDS
   */
  private is_initialized: boolean = false;

  renderer!: THREE.WebGLRenderer;

  scene!: THREE.Scene;

  transform_controls!: TransformControls;

  light_directional!: THREE.DirectionalLight;
  light_ambient!: THREE.AmbientLight;

  helper_grid!: THREE.GridHelper;

  body_controller!: OpenPoseBodyController;

  billboard_controller!: OpenPoseBillboardController;

  camera_controller!: OpenPoseCameraController;

  projection_controller!: OpenPoseBodyProjectionController;

  private animation_frame_id!: number;

  /*
   * EVENTS
   */

  public on_resize() {
    const canvas = this.renderer.domElement;
    const size = new THREE.Vector2();
    this.renderer.getSize(size);

    if (size.width === canvas.clientWidth && size.height === canvas.clientHeight) return;
    if (canvas.clientWidth === 0) return;
    if (canvas.clientHeight === 0) return;

    this.update_camera_projection();
    this.renderer.setSize(canvas.clientWidth, canvas.clientHeight, false);
  }

  /*
   * PROPERTIES
   */

  private get_canvas_aspect_ratio(): number {
    const canvas = this.renderer.domElement;

    return Math.max(1e-6, canvas.clientWidth / Math.max(1, canvas.clientHeight));
  }

  public get_camera_current(): THREE.Camera {
    return this.transform_controls.camera;
  }

  public set_camera(camera: THREE.Camera) {
    this.transform_controls.camera = camera;
  }

  public get_current_canvas(): HTMLCanvasElement {
    return this.renderer.domElement;
  }

  /*
   * METHODS
   */

  constructor() {
    this.animation_frame_id = NaN;
  }

  /*
   * initializers
   */

  init(canvas: HTMLCanvasElement) {
    if (this.is_initialized) return;

    this.scene = new THREE.Scene();

    this.init_renderer(canvas);

    const body = open_pose_body_create_template();
    this.body_controller = new OpenPoseBodyController(body);
    this.body_controller.create_or_update_bones(true);
    this.scene.add(body);

    this.billboard_controller = new OpenPoseBillboardController();
    this.billboard_controller.create_billboard();

    this.camera_controller = new OpenPoseCameraController();

    this.projection_controller = new OpenPoseBodyProjectionController();

    this.init_helpers();
    this.init_transform_controls();
    this.init_directional_light();
    this.init_ambient_light();

    this.is_initialized = true;
    store.dispatch(LOCAL__ui_set_boolean({ key: 'is_service_open_pose_init', value: true }));
  }

  private init_renderer(canvas: HTMLCanvasElement) {
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    this.renderer.setClearColor(CLEAR_COLOR, 1.0);

    const width = Math.max(1, canvas.clientWidth);
    const height = Math.max(1, canvas.clientHeight);

    this.renderer.setSize(width, height, false);
  }

  private init_helpers() {
    this.helper_grid = new THREE.GridHelper(GRID_HELPER_SIZE, GRID_HELPER_DIVISIONS);

    this.scene.add(this.helper_grid);
  }

  private init_transform_controls() {
    const camera = this.create_camera_default();

    this.transform_controls = new TransformControls(camera, this.renderer.domElement);
    this.transform_controls.setMode(EOpenPoseTransformControlsMode.ROTATE);
    this.transform_controls.setSize(ConstantsOpenPose.TRANSFORM_CONTROLS_SIZE);
    this.transform_controls.setSpace(EOpenPoseTransformControlsSpace.LOCAL);

    this.scene.add(this.transform_controls as unknown as THREE.Object3D);
  }

  private init_directional_light() {
    this.light_directional = new THREE.DirectionalLight(DIRECTIONSL_LIGHT_COLOR, DIRECTIONSL_LIGHT_INTENSITY);
    this.light_directional.position.set(DIRECTIONAL_LIGHT_INIT_POSITION_X, DIRECTIONAL_LIGHT_INIT_POSITION_Y, DIRECTIONAL_LIGHT_INIT_POSITION_Z);
    this.scene.add(this.light_directional);
  }

  private init_ambient_light() {
    this.light_ambient = new THREE.AmbientLight(AMBIENT_LIGHT_COLOR, AMBIENT_LIGHT_INTENSITY);
    this.scene.add(this.light_ambient);
  }

  private create_camera_default() {
    const out = new THREE.PerspectiveCamera(CAMERA_INIT_FOV, this.get_canvas_aspect_ratio(), CAMERA_INIT_NEAR, CAMERA_INIT_FAR);

    out.position.set(CAMERA_INIT_POSITION_X, CAMERA_INIT_POSITION_Y, CAMERA_INIT_POSITION_Z);
    out.lookAt(0, 0, 0);
    out.updateProjectionMatrix();

    return out;
  }

  /*
   * CORE LOGIC
   */

  private update_camera_projection() {
    const camera = this.get_camera_current();
    const aspect = this.get_canvas_aspect_ratio();

    if (camera instanceof THREE.PerspectiveCamera) {
      camera.aspect = aspect;
      camera.updateProjectionMatrix();
    } else if (camera instanceof THREE.OrthographicCamera) {
      const height = camera.top - camera.bottom;
      const width = height * aspect;
      const center_x = (camera.left + camera.right) / 2;

      camera.left = center_x - width / 2;
      camera.right = center_x + width / 2;
      camera.updateProjectionMatrix();
    } else {
      not_implemented();
    }
  }

  public reset_transform_controls() {
    const camera_current = this.get_camera_current();
    this.scene.remove(this.transform_controls as unknown as THREE.Object3D);

    this.transform_controls.dispose();

    this.transform_controls = new TransformControls(camera_current, this.renderer.domElement);
    this.transform_controls.setMode(EOpenPoseTransformControlsMode.ROTATE);
    this.transform_controls.setSpace(EOpenPoseTransformControlsSpace.LOCAL);

    this.scene.add(this.transform_controls as unknown as THREE.Object3D);
  }

  public render() {
    const canvas_current = this.renderer.domElement;
    const width = canvas_current.clientWidth;
    const height = canvas_current.clientHeight;

    this.renderer.setViewport(0, 0, width, height);
    this.renderer.setScissor(0, 0, width, height);
    this.renderer.setScissorTest(true);
    this.renderer.render(this.scene, this.get_camera_current());
  }

  public add_dom_event_listener<K extends keyof HTMLElementEventMap>(type: K, handler: (this: HTMLCanvasElement, event: HTMLElementEventMap[K]) => any) {
    this.renderer.domElement.addEventListener(type, handler as EventListener);
  }

  public remove_dom_event_listener<K extends keyof HTMLElementEventMap>(type: K, handler: (this: HTMLCanvasElement, event: HTMLElementEventMap[K]) => any) {
    this.renderer.domElement.removeEventListener(type, handler as EventListener);
  }

  public start_main_render_loop(on_tick: (time_ms: number) => void) {
    // Define the per-frame callback that drives the render loop.
    // The browser calls this function roughly once per screen refresh
    // and passes a high-resolution timestamp (DOMHighResTimeStamp).
    const loop = (time_ms: number) => {
      // Schedule the next frame *first* and keep the returned handle.
      // Storing the id lets us cancel the loop later in stop_animation().
      // Scheduling up-front also reduces the chance of drift if on_tick/render() are heavy.
      this.animation_frame_id = requestAnimationFrame(loop);

      // Give the host application a chance to update simulation state for this frame.
      // Keep this callback fast; long-running work here will drop frames.
      on_tick(time_ms);

      // Ensure the renderer and camera projection match the current CSS pixel size
      // of the canvas (e.g., when the window or container is resized).
      this.on_resize();

      // Draw the current scene using the active camera.
      this.render();
    };

    // Kick off the render loop by requesting the very first frame and
    // keeping its id so we can cancel it if needed.
    this.animation_frame_id = requestAnimationFrame(loop);
  }

  public stop_main_render_loop() {
    cancelAnimationFrame(this.animation_frame_id);

    this.animation_frame_id = NaN;
  }

  public dispose() {
    this.stop_main_render_loop();

    this.billboard_controller.dispose();
    this.camera_controller.dispose();

    this.scene.remove(this.transform_controls as unknown as THREE.Object3D);
    this.transform_controls.dispose();

    this.scene.remove(this.helper_grid);
    this.helper_grid.dispose();

    this.scene.remove(this.light_directional);
    this.light_directional.dispose();

    this.scene.remove(this.light_ambient);
    this.light_ambient.dispose();

    this.renderer.dispose();

    this.is_initialized = false;
    store.dispatch(LOCAL__ui_set_boolean({ key: 'is_service_open_pose_init', value: false }));
  }
}

export const service_open_pose = new ServiceOpenPose();
